import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Slider } from "antd";
import { getHeatmapMeta, getHeatmapValues, HeatmapMeta } from "../../api/deepzoomAPI";

interface HeatmapOverlayProps {
  viewer: any;
  postId: number;
  filename: string;
}

interface HeatmapValues {
  value: Uint8ClampedArray; // 0~255, 슬라이드 내 상대 attention
  mask: Uint8ClampedArray; // 255 = 조직
  width: number;
  height: number;
}

const SOFT_RATIO = 0.12; // 하한 위쪽으로 이 비율만큼 서서히 불투명해진다 (하드컷 방지)

// jet: 참고 그림(Relative relevance 컬러바)과 같은 계열. 0xAABBGGRR (little-endian RGBA)
const JET = (() => {
  const clamp = (x: number) => Math.min(1, Math.max(0, x));
  const lut = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    const t = i / 255;
    const r = Math.round(255 * clamp(1.5 - Math.abs(4 * t - 3)));
    const g = Math.round(255 * clamp(1.5 - Math.abs(4 * t - 2)));
    const b = Math.round(255 * clamp(1.5 - Math.abs(4 * t - 1)));
    lut[i] = ((255 << 24) | (b << 16) | (g << 8) | r) >>> 0;
  }
  return lut;
})();

const HeatmapOverlay = ({ viewer, postId, filename }: HeatmapOverlayProps) => {
  const [meta, setMeta] = useState<HeatmapMeta | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState<[number, number]>([0, 100]); // 값 창(%)
  const [opacity, setOpacity] = useState(0.55);

  const valuesRef = useRef<HeatmapValues | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const frameRef = useRef(0);
  const paramsRef = useRef({ range, opacity });
  paramsRef.current = { range, opacity };

  // 슬라이드가 바뀌면 상태 초기화 후 meta 조회 (결과 없으면 버튼 자체를 숨긴다)
  useEffect(() => {
    let cancelled = false;
    setMeta(null);
    setEnabled(false);
    valuesRef.current = null;
    if (!postId || !filename) return;
    getHeatmapMeta(postId, filename).then((m) => {
      if (!cancelled) setMeta(m);
    });
    return () => {
      cancelled = true;
    };
  }, [postId, filename]);

  const paint = useCallback(() => {
    const v = valuesRef.current;
    const canvas = canvasRef.current;
    if (!v || !canvas) return;
    if (canvas.width !== v.width || canvas.height !== v.height) {
      canvas.width = v.width;
      canvas.height = v.height;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { range: [loPct, hiPct], opacity: op } = paramsRef.current;
    const lo = (loPct / 100) * 255;
    const hi = Math.max(lo + 1, (hiPct / 100) * 255);
    const soft = Math.max(4, (hi - lo) * SOFT_RATIO);

    const img = ctx.createImageData(v.width, v.height);
    const out = new Uint32Array(img.data.buffer);
    const n = v.width * v.height;
    for (let i = 0; i < n; i++) {
      if (v.mask[i] === 0) continue; // 비조직은 투명
      const val = v.value[i];
      if (val < lo) continue;
      const t = Math.min(1, (val - lo) / (hi - lo));
      const s = Math.min(1, (val - lo) / soft);
      const a = Math.round(255 * op * (s * s * (3 - 2 * s)));
      out[i] = ((a << 24) | (JET[Math.round(t * 255)] & 0x00ffffff)) >>> 0;
    }
    ctx.putImageData(img, 0, 0);
  }, []);

  const schedulePaint = useCallback(() => {
    cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(paint);
  }, [paint]);

  // 오버레이 켜기/끄기
  useEffect(() => {
    if (!viewer || !meta || !enabled) return;
    let cancelled = false;

    const container = document.createElement("div");
    container.style.pointerEvents = "none"; // 어노테이션/클릭 이벤트를 가리지 않는다
    const canvas = document.createElement("canvas");
    canvas.style.cssText = "width:100%;height:100%;display:block;";
    container.appendChild(canvas);
    overlayRef.current = container;
    canvasRef.current = canvas;

    // level-0 좌표 -> OSD viewport 좌표. DZI 는 bounds 영역만 담고 있으므로 bounds 원점을 빼고,
    // DZI 크기와 bounds 크기가 다르면 비율로 보정한다.
    const place = () => {
      const item = viewer.world?.getItemAt?.(0);
      if (!item) return;
      const s = item.getContentSize().x / meta.boundsWidth;
      const rect = item.imageToViewportRectangle(
        (meta.originX - meta.boundsX) * s,
        (meta.originY - meta.boundsY) * s,
        meta.extentWidth * s,
        meta.extentHeight * s
      );
      try {
        viewer.removeOverlay(container);
      } catch {
        /* 아직 추가되지 않음 */
      }
      viewer.addOverlay({ element: container, location: rect });
    };

    const load = async () => {
      if (!valuesRef.current) {
        setLoading(true);
        try {
          const blob = await getHeatmapValues(postId, filename);
          const bmp = await createImageBitmap(blob, {
            colorSpaceConversion: "none",
            premultiplyAlpha: "none",
          });
          const tmp = document.createElement("canvas");
          tmp.width = bmp.width;
          tmp.height = bmp.height;
          const tctx = tmp.getContext("2d", { willReadFrequently: true });
          if (!tctx) return;
          tctx.drawImage(bmp, 0, 0);
          const px = tctx.getImageData(0, 0, bmp.width, bmp.height).data;
          const n = bmp.width * bmp.height;
          const value = new Uint8ClampedArray(n);
          const mask = new Uint8ClampedArray(n);
          for (let i = 0; i < n; i++) {
            value[i] = px[i * 4]; // R
            mask[i] = px[i * 4 + 1]; // G
          }
          valuesRef.current = { value, mask, width: bmp.width, height: bmp.height };
        } catch (e) {
          console.error("히트맵 로드 실패:", e);
          return;
        } finally {
          if (!cancelled) setLoading(false);
        }
      }
      if (cancelled) return;
      place();
      schedulePaint();
    };

    // ICC 전환 등으로 viewer.open() 이 다시 호출되면 오버레이가 지워지므로 다시 붙인다
    const onOpen = () => place();
    viewer.addHandler("open", onOpen);
    load();

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameRef.current);
      viewer.removeHandler("open", onOpen);
      try {
        viewer.removeOverlay(container);
      } catch {
        /* viewer 가 이미 destroy 됨 */
      }
      overlayRef.current = null;
      canvasRef.current = null;
    };
  }, [viewer, meta, enabled, postId, filename, schedulePaint]);

  // 슬라이더 값이 바뀌면 다시 칠하기 (서버 왕복 없음)
  useEffect(() => {
    if (enabled) schedulePaint();
  }, [range, opacity, enabled, schedulePaint]);

  if (!meta) return null;

  return (
    <>
      <StToggleButton $isActive={enabled} onClick={() => setEnabled((v) => !v)}>
        {loading ? "Heatmap…" : "Heatmap"}
      </StToggleButton>
      {enabled && (
        <StPanel onMouseDown={(e) => e.stopPropagation()}>
          {meta.prediction && (
            <StPrediction>
              <div>
                AI 판정: <b>{meta.prediction.pred}</b> (
                {Math.round((meta.prediction.probs[meta.prediction.pred] ?? 0) * 100)}%) · 분석 패치{" "}
                {meta.prediction.nPatches.toLocaleString()}개
              </div>
              <StNotice>※ 참고용 AI 결과이며 최종 진단을 대체하지 않습니다</StNotice>
            </StPrediction>
          )}
          <StRow>
            <span>AI 주목도 (슬라이드 내 상대값)</span>
          </StRow>
          <StLegend />
          <StLegendLabels>
            <span>Low</span>
            <span>High</span>
          </StLegendLabels>
          <StRow>
            <span>표시 범위 {range[0]}–{range[1]}%</span>
            <Slider range min={0} max={100} value={range} onChange={(v) => setRange(v as [number, number])} />
          </StRow>
          <StRow>
            <span>투명도 {Math.round(opacity * 100)}%</span>
            <Slider min={0.05} max={1} step={0.05} value={opacity} onChange={setOpacity} />
          </StRow>
        </StPanel>
      )}
    </>
  );
};

export default HeatmapOverlay;

const StToggleButton = styled.button<{ $isActive: boolean }>`
  background-color: ${(p) => (p.$isActive ? "rgba(24, 144, 255, 0.8)" : "rgba(0, 0, 0, 0.5)")};
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 0.6rem;
  padding: 0 0.4rem;

  &:hover {
    background-color: ${(p) => (p.$isActive ? "rgba(24, 144, 255, 0.95)" : "rgba(0, 0, 0, 0.75)")};
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const StPanel = styled.div`
  position: absolute;
  right: 0;
  bottom: calc(100% + 8px);
  width: 240px;
  padding: 10px 12px 2px;
  background: rgba(0, 0, 0, 0.72);
  color: white;
  border-radius: 6px;
  font-size: 0.7rem;
`;

const StRow = styled.div`
  margin-bottom: 6px;
`;

const StPrediction = styled.div`
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  line-height: 1.4;
`;

const StNotice = styled.div`
  margin-top: 2px;
  font-size: 0.6rem;
  opacity: 0.7;
`;

// jet 컬러바 (Low -> High)
const StLegend = styled.div`
  height: 10px;
  border-radius: 2px;
  background: linear-gradient(to right, #000080, #0000ff, #00ffff, #ffff00, #ff0000, #800000);
`;

const StLegendLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 2px 0 8px;
  font-size: 0.65rem;
  opacity: 0.85;
`;
